import React, { useState, useRef, useLayoutEffect } from 'react';

export default function BlueEnvelopeForm({ virtual = false }) {
  // Dynamically fit the single-line "DRIVER WITH SPECIAL NEEDS" title to its
  // container. font-black falls back to wider faces on Windows (e.g. Arial
  // Black) than on macOS, which would otherwise clip the trailing "S" because
  // of `whitespace-nowrap`. Re-fits on resize, font load, and print.
  const titleRef = useRef(null);

  useLayoutEffect(() => {
    let frame = 0;
    const fit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const node = titleRef.current;
        if (!node) return;
        node.style.fontSize = '';
        const overflow = node.scrollWidth - node.clientWidth;
        if (overflow > 1) {
          const naturalPx = parseFloat(window.getComputedStyle(node).fontSize);
          const ratio = node.clientWidth / node.scrollWidth;
          const fittedPx = Math.max(12, naturalPx * ratio * 0.985);
          node.style.fontSize = `${fittedPx}px`;
        }
      });
    };

    fit();

    const node = titleRef.current;
    const observed = node?.parentElement || node;
    const ro = observed && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(fit)
      : null;
    if (ro && observed) ro.observe(observed);
    window.addEventListener('resize', fit);
    window.addEventListener('beforeprint', fit);
    window.addEventListener('afterprint', fit);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fit).catch(() => {});
    }

    return () => {
      cancelAnimationFrame(frame);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', fit);
      window.removeEventListener('beforeprint', fit);
      window.removeEventListener('afterprint', fit);
    };
  }, []);

  const [formData, setFormData] = useState({
    driverName: '',
    emergencyContactType: '',
    emergencyContactName: '',
    phoneAreaCode: '',
    phoneNumber: '',
    conditions: [],
    otherCondition1: '',
    otherCondition2: '',
  });

  const conditionsLeft = [
    { id: 'autism', label: 'Autism Spectrum Disorder' },
    { id: 'adhd', label: 'ADHD' },
    { id: 'anxiety', label: 'Anxiety' },
    { id: 'ptsd', label: 'PTSD / Sensory Sensitivities' },
    { id: 'hearing', label: 'Hearing Differences' },
  ];

  const conditionsRight = [
    { id: 'intellectual', label: 'Intellectual / Developmental Disability' },
    { id: 'speech', label: 'Speech or Non-Verbal Communication' },
    { id: 'verbal', label: 'Difficulty processing verbal instructions' },
    { id: 'medical', label: 'Medical / neurological condition affecting communication' },
    { id: 'extraTime', label: 'May need extra time to respond' },
  ];

  const emergencyContactOptions = ['Mother', 'Father', 'Sister', 'Brother', 'Friend', 'Other'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleConditionChange = (conditionId) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.includes(conditionId)
        ? prev.conditions.filter(c => c !== conditionId)
        : [...prev.conditions, conditionId]
    }));
  };

  // Form is not submitted or stored (sensitive data). User fills and prints as-is.
  const handleSubmit = (e) => {
    e.preventDefault();
    handlePrint();
  };

  const handleReset = () => {
    setFormData({
      driverName: '',
      emergencyContactType: '',
      emergencyContactName: '',
      phoneAreaCode: '',
      phoneNumber: '',
      conditions: [],
      otherCondition1: '',
      otherCondition2: '',
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Print-only styles: hide everything except the form when printing; keep to one page
  const printStyles = `
    .black-check-box:checked {
      border-color: transparent !important;
      background-color: black !important;
    }
    @media print {
      @page {
        size: letter;
        margin: 0.25in !important; 
        backgroundColor: #daf0fd !important;
      }
      body * {
        visibility: hidden;
        height: 0 !important;
        min-height: 0 !important;
        max-height: 0 !important;
        overflow: hidden !important;
        position: absolute !important;
        left: -9999px !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .black-check-box:checked {
        border-color: transparent !important;
        background-color: black !important;
      }
      .blue-envelope-print-area,
      .blue-envelope-print-area * {
        visibility: visible;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        position: static !important;
        left: auto !important;
        backgroundColor: #afc6e1 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .blue-envelope-print-area {
        position: fixed !important;
        left: 0 !important;
        top: 0 !important;
        right: 0 !important;
        width: 100% !important;
        margin: 0 !important;
        page-break-after: avoid;
        page-break-inside: avoid;
      }
      .blue-envelope-no-print {
        visibility: hidden !important;
        display: none !important;
        height: 0 !important;
        min-height: 0 !important;
        overflow: hidden !important;
      }
      .blue-envelope-two-cols {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 0.25rem 0.25rem !important;
      }
      .blue-envelope-two-cols > * {
        display: flex !important;
        flex-direction: column !important;
        flex-wrap: nowrap !important;
        gap: 0.2rem !important;
      }
      .blue-envelope-print-area input[type="checkbox"] {
        width: 1rem !important;
        min-width: 1rem !important;
        height: 1rem !important;
        min-height: 1rem !important;
        border: 2px solid #000 !important;
        border-radius: 2px !important;
        box-sizing: border-box !important;
        flex-shrink: 0 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .blue-envelope-two-cols label {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        white-space: normal !important;
        gap: 0.375rem !important;
      }
      .blue-envelope-emergency-contact {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: wrap !important;
        align-items: center !important;
        gap: 0.5rem 1rem !important;
      }
      .blue-envelope-emergency-contact > label {
        display: inline-flex !important;
        align-items: center !important;
      }
      .blue-envelope-print-area img {
        visibility: visible !important;
        display: inline-block !important;
        width: 3rem !important;
        min-width: 3rem !important;
        height: 3rem !important;
        min-height: 3rem !important;
        max-width: none !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .blue-envelope-print-area a[href] img {
        visibility: visible !important;
        display: inline-block !important;
      }
      .blue-envelope-logo-wrap,
      .blue-envelope-logo-wrap * {
        visibility: visible !important;
      }
      .blue-envelope-logo-wrap {
        display: flex !important;
        flex-shrink: 0 !important;
      }
      .blue-envelope-logo-wrap > div {
        width: 3rem !important;
        min-width: 3rem !important;
        height: 3rem !important;
        min-height: 3rem !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .blue-envelope-print-area .blue-envelope-form-title {
        font-size: 3.07rem;
        font-weight: 900 !important;
        letter-spacing: -0.025em !important;
        color: #111827 !important;
      }
      .blue-envelope-print-area .blue-envelope-form-subtitle {
        font-size: 1.125rem !important;
        font-weight: 900 !important;
        color: #111827 !important;
      }
    }
  `;

  const Checkbox = ({ checked, onChange, label, hasInput, inputValue, onInputChange }) => (
    <label className={`flex flex-row items-start gap-1 cursor-pointer text-sm leading-tight ${virtual ? 'items-center' : 'pointer-events-nonex'}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className={`m-0 w-4 h-4 black-check-box  rounded-sm accent-blue-600 cursor-pointer flex-shrink-0 ${virtual ? 'border-1 border-gray-400 ring-0 checked:ring-0' : ' !bg-transparent border border-black'}`}
      />
      <span className="text-black flex flex-row items-center gap-1 whitespace-normal md:whitespace-nowrap">
        {label}
        {hasInput && (
          <input
            type="text"
            value={inputValue}
            onChange={onInputChange}
            className={`m-0  ml-1 border-b  !bg-transparent outline-none w-32 !text-gray-800 ${virtual ? 'border-b !border-gray-400 focus:border-gray-900 focus:ring-0' : 'cursor-events-none h-4 !bg-transparent border-t-0 border-l-0 border-r-0 border-b-1 border-black'}`}
          />
        )}
      </span>
    </label>
  );

  return (
    <div className="relative min-h-screen p-4 md:p-6" style={{ backgroundColor: '#313131' }}>
      <style>{printStyles}</style>
      {/* Print button at top right (form page only) */}
      {!virtual && (
        <button
          type="button"
          onClick={handlePrint}
          className="blue-envelope-no-print absolute top-2 md:top-4 right-4 md:right-6 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded transition-colors z-10"
        >
          Print
        </button>
      )}
      <form onSubmit={handleSubmit} className="blue-envelope-print-area w-full md:w-[50rem] mx-auto p-4 md:p-[3rem] bg-sky-100 mt-11 md:mt-0" >
        {/* Header – keep size in print via .blue-envelope-form-title */}
        <h1
          ref={titleRef}
          className="blue-envelope-form-title w-full text-start ml-[-3px] font-black text-gray-900 tracking-tight mb-1 whitespace-nowrap text-[clamp(1rem,5vw,3.07rem)]"
        >
          DRIVER WITH SPECIAL NEEDS
        </h1>
        <p className="blue-envelope-form-subtitle font-black text-lg my-4">
          Driver may need extra time and simple, clear instructions during this stop.
        </p>

        {/* Driver Information Section */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Driver Information</h2>

          {/* My Name */}
          <div className="mb-5">
            <label className={`flex ${virtual ? 'items-end' : 'items-end pointer-events-nonex'} gap-2`}>
              <span className={`text-black text-sm whitespace-nowrap ${virtual ? '' : 'mb-[-2px]'}`}>My Name:</span>
              <input
                type="text"
                name="driverName"
                value={formData.driverName}
                onChange={handleInputChange}
                className={`m-0 flex-1 ${virtual ? 'border-b border-gray-400 bg-transparent outline-none py-1 text-gray-800 focus:border-purple-900 focus:ring-0' : 'pointer-events-nonex !bg-transparent border-t-0 border-l-0 border-r-0 border-b-1 border-black'}`}
                required
              />
            </label>
          </div>

          {/* Emergency Contact Type */}
          <div className="mb-4 ">
            <div className="blue-envelope-emergency-contact flex flex-row flex-wrap items-center gap-x-4 gap-y-2 ">
              <span className="text-black text-sm">Emergency Contact:</span>
              {emergencyContactOptions.map(option => (
                <label key={option} className={`flex items-center gap-1.5 cursor-pointer text-sm leading-tight ${virtual ? '' : 'pointer-events-nonex'}`}>
                  <input
                    type="checkbox"
                    checked={formData.emergencyContactType === option}
                    onChange={() => setFormData(prev => ({ ...prev, emergencyContactType: option }))}
                    className={`m-0 w-4 h-4 black-check-box rounded-sm cursor-pointer flex-shrink-0 border ${virtual ? 'border-gray-400 ring-0 checked:ring-0 checked:bg-transparent checked:border checked:border-gray-500 checked:text-gray-700' : '!bg-transparent border-black checked:bg-transparent checked:border checked:border-gray-600 checked:text-gray-700'}`}
                  />
                  <span className="text-black">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Emergency Contact Name and Phone */}
          <div className="flex flex-wrap gap-x-2 gap-y-4">
            <label className={`h-10 md:h-auto flex items-start md:items-end gap-2 flex-1 flex-col md:flex-row w-full md:w-auto min-w-64 ${virtual ? '' : 'pointer-events-nonex'}`}>
              <span className="text-black text-sm whitespace-nowrap">Emergency Contact Name:</span>
              <input
                type="text"
                name="emergencyContactName"
                value={formData.emergencyContactName}
                onChange={handleInputChange}
                className={` m-0 md:flex flex-1 w-full md:w-auto ${virtual ? 'border-b border-gray-400 bg-transparent outline-none py-1 text-gray-800 focus:border-purple-900 focus:ring-0' : 'h-2 md:h-6 !bg-transparent border-t-0 border-l-0 border-r-0 border-b-1 border-black'}`}
                required
              />
            </label>
            <label className={`flex items-end gap-1 ${virtual ? '' : 'pointer-events-nonex'}`}>
              <span className="text-black text-sm whitespace-nowrap">Phone: (</span>
              <input
                type="tel"
                name="phoneAreaCode"
                value={formData.phoneAreaCode}
                onChange={handleInputChange}
                maxLength={3}
                className={`m-0 w-10 border-b  bg-transparent outline-none py-1 text-center text-gray-800 ${virtual ? 'border-b border-gray-400 focus:border-purple-900 focus:ring-0' : 'pointer-events-nonex h-6 !bg-transparent border-t-0 border-l-0 border-r-0 border-b-1 border-black'}`}
                required
              />
              <span className="text-black text-sm">)</span>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`m-0 w-32 border-b  bg-transparent outline-none py-1 text-gray-800 ${virtual ? 'border-b border-gray-400 focus:border-purple-900 focus:ring-0' : 'pointer-events-nonex h-6 !bg-transparent border-t-0 border-l-0 border-r-0 border-b-1 border-black'}`}
                required
              />
            </label>
          </div>
        </section>

        {/* Information About Me Section */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">
            Information About Me <span >(check all that apply)</span>
          </h2>

          <div className="blue-envelope-two-cols grid grid-cols-1 md:grid-cols-2 gap-x-1 gap-y-2">
            {/* Left Column */}
            <div className="flex flex-col gap-y-[5px]">
              {conditionsLeft.map(option => (
                <Checkbox
                  key={option.id}
                  checked={formData.conditions.includes(option.id)}
                  onChange={() => handleConditionChange(option.id)}
                  label={option.label}
                />
              ))}
              <Checkbox
                checked={!!formData.otherCondition1}
                onChange={() => { }}
                label="Other"
                hasInput
                inputValue={formData.otherCondition1}
                onInputChange={(e) => setFormData(prev => ({ ...prev, otherCondition1: e.target.value }))}
              />
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-y-[5px]">
              {conditionsRight.map(option => (
                <Checkbox
                  key={option.id}
                  checked={formData.conditions.includes(option.id)}
                  onChange={() => handleConditionChange(option.id)}
                  label={option.label}
                />
              ))}
              <Checkbox
                checked={!!formData.otherCondition2}
                onChange={() => { }}
                label="Other"
                hasInput
                inputValue={formData.otherCondition2}
                onInputChange={(e) => setFormData(prev => ({ ...prev, otherCondition2: e.target.value }))}
              />
            </div>
          </div>
        </section>

        {/* For Law Enforcement Section */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-2">For Law Enforcement and First Responders</h2>
          <p className="text-sm text-black mb-2">
            This information is provided to support safe and clear communication during a traffic stop.
          </p>

          <div className="flex flex-wrap gap-8">
            <ul className="text-sm text-black space-y-1 flex-1">
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>Driver may appear anxious or overwhelmed</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>Bright lights, sirens, or radios may increase stress</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>Eye contact, body movement, or speech may be atypical</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>Give one instruction at a time</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>Use clear, simple language</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>Allow extra time for responses</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0 text-[1.2rem]">•</span>
                <span>Avoid unnecessary noise or commands when possible</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>Clearly state when the stop is complete</span>
              </li>
              <li className="flex items-end gap-1 m-0">
                <span className="mt-0  text-[1.2rem]">•</span>
                <span>If needed, consider calling the listed emergency contact</span>
              </li>
            </ul>



          </div>
        </section>

        {/* For the Driver Section – no line below */}
        <section className="mb-4 border-b-0">
          <h2 className="text-base font-bold text-gray-900 mb-1">For the Driver</h2>
          <p className="text-sm text-black">
            This is a voluntary personal communication aid and is not an official government document.
            It does not change your legal rights or obligations during a traffic stops.
          </p>
        </section>

        {/* Legal Disclaimer – no line above */}
        <section className="mb-6 border-t-0 pt-4">
          <div className="flex items-start gap-3">
            <div className="blue-envelope-logo-wrap flex flex-col items-center flex-shrink-0">
              <div className="w-12 h-12 min-w-[3rem] min-h-[3rem] rounded-full bg-gradient-to-br from-yellow-400 via-green-400 to-blue-500 flex items-center justify-center">
                {/* <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="8" r="3" />
                  <circle cx="7" cy="14" r="2.5" />
                  <circle cx="17" cy="14" r="2.5" />
                  <circle cx="12" cy="18" r="2.5" />
                </svg> */}
                <a href="/" className="block">
                  <img className="h-12 w-12 object-contain" src="/logo.png" alt="wefayo logo" style={{ maxWidth: 'unset' }} />
                </a>
              </div>
              {/* <span className="text-xs font-bold mt-1">
                <span className="text-orange-500">we</span>
                <span className="text-blue-600">fayo</span>
              </span> */}

              <a href="/" class="menu-link-logo-cls hidden md:inline-block">
                <span class="text-sm">
                  <b class="logo-font menu-link-logo-cls">we</b>
                  <b class="logo-font logo-light menu-link-logo-cls">fayo</b>
                </span>
              </a>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Legal Disclaimer</h3>
              <p className="text-xs text-black">
                This document is provided as a free courtesy from Wefayo's Blue Envelope Program®. It is not a
                legal document and is intended only to aid communication between parties. © 2026 Wefayo LLC.
                This document may be shared or reprinted. Wefayo.com BlueEnvelopeProgram.com
              </p>
            </div>
          </div>
        </section>

        {/* Submit Buttons (virtual form only) – hidden when printing */}
        {virtual && (
          <div className="blue-envelope-no-print flex gap-4 pt-4 border-t border-blue-200">
            <button
              type="submit"
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 px-8 rounded transition-colors"
            >
              Submit Form
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2.5 px-6 rounded transition-colors"
            >
              Clear
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
